/* eslint-disable consistent-return */
import {HEALTH_POINT} from 'constants/HealthConstant'
import {
  selectLanguageReport,
  getCurrentLanguageReport,
} from 'features/languageReport/languageReportSlice'
import {useAppSelector} from 'hooks'
import i18n from 'i18n'
import {useMemo} from 'react'

export default function useLanguage(type: 'mci' | 'eeg' | 'hrv' | 'health') {
  const isEEG = type === 'eeg'
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )

  const currentLanguageReport = useMemo(() => {
    return getCurrentLanguageReport(languageReport, i18n.language)
  }, [languageReport, i18n])

  const percentile = useMemo(() => {
    if (!isEEG) return HEALTH_POINT
    return (
      currentLanguageReport?.eeg?.brain_aging.threshold || {
        low: 16,
        high: 84,
      }
    )
  }, [currentLanguageReport, isEEG])

  const scoreUnit = useMemo(() => {
    return currentLanguageReport?.health?.common.score_unit.t_score
  }, [currentLanguageReport, isEEG])

  const textFiveMajorAPI = useMemo(() => {
    if (currentLanguageReport?.health?.five_major_features)
      return currentLanguageReport?.health?.five_major_features
    return null
  }, [currentLanguageReport])

  const textMciAPI = useMemo(() => {
    if (currentLanguageReport?.mci) return currentLanguageReport?.mci
    return null
  }, [currentLanguageReport])

  const textEegAPI = useMemo(() => {
    if (currentLanguageReport?.eeg) return currentLanguageReport?.eeg
    return null
  }, [currentLanguageReport])

  const textHrvAPI = useMemo(() => {
    if (currentLanguageReport?.hrv) return currentLanguageReport?.hrv
    return null
  }, [currentLanguageReport])

  const textHealthAPI = useMemo(() => {
    if (currentLanguageReport?.health) return currentLanguageReport?.health
    return null
  }, [currentLanguageReport])

  const textHeaderAPI = useMemo(() => {
    if (type === 'mci') {
      return currentLanguageReport?.mci?.header
    }
    if (type === 'eeg') {
      return currentLanguageReport?.eeg?.header
    }
    if (type === 'hrv') {
      return currentLanguageReport?.hrv?.header
    }
    if (type === 'health') {
      return currentLanguageReport?.health?.header
    }
  }, [currentLanguageReport, type])

  const healthDifferenceThreshold = useMemo(
    () =>
      currentLanguageReport?.health?.brain_connectivity.difference_threshold
        .t_score.value || 10,
    [currentLanguageReport],
  )

  return {
    languageReport,
    currentLanguageReport,
    percentile,
    textFiveMajorAPI,
    textMciAPI,
    textEegAPI,
    textHrvAPI,
    textHealthAPI,
    scoreUnit,
    textHeaderAPI,
    healthDifferenceThreshold,
  }
}
